// Reconciliation for conversational prescription drafting (C9,
// docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2). "Draft with AI" is a
// thread: turn 1 proposes fresh lines (unchanged, appended as today); every
// later turn sends the AI its own prior output (formatItemsAsRxLines) plus
// the doctor's instruction, and gets back a fresh full list to reconcile
// against the table — not a diff/patch, so no new prompt grammar is needed.
//
// Ownership marker: `ai_proposal_id != null`. That column already exists
// precisely to mark "this row came from AI", and is set synchronously
// (record-then-tag) before the next turn can start, so it's a reliable
// identity across turns with no separate tracking state. Manual rows
// (`ai_proposal_id == null`) are never inspected or touched here — they are
// outside the AI thread's scope entirely, preserving "AI proposes, doctor
// approves."
import type { PrescriptionItemInput } from './types';

// Client mirror of backend/utils/prescriptionAiProposals.js's OUTCOME_FIELDS,
// plus frequency_text (a line sets either frequency_code or frequency_text) —
// an independent port, same reasoning as docxGenerator.js/aiEditObservation.js
// already being two independent backend ports of the same heading rule.
const COMPARE_FIELDS = [
  'drug_name', 'strength', 'dose_quantity', 'dose_unit', 'route',
  'frequency_code', 'frequency_text', 'duration_value', 'duration_unit',
] as const;

function normalize(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const s = String(value).replace(/\s+/g, '').toLowerCase();
  if (s === '') return null;
  return /^-?\d+(\.\d+)?$/.test(s) ? String(Number(s)) : s;
}

function nameKey(item: PrescriptionItemInput): string {
  return item.drug_name.trim().toLowerCase();
}

function linesEqual(a: PrescriptionItemInput, b: PrescriptionItemInput): boolean {
  return COMPARE_FIELDS.every((field) => normalize(a[field]) === normalize(b[field]));
}

/** Inverse of parseAiPrescriptionLines — only the AI-owned rows, so a
 * follow-up turn revises the model's own prior output, not raw table state. */
export function formatItemsAsRxLines(items: PrescriptionItemInput[]): string {
  return items
    .filter((it) => it.ai_proposal_id != null && it.drug_name.trim())
    .map((it) => {
      const strength = it.strength ? ` ${it.strength}` : '';
      const dosePart = it.dose_quantity != null ? `${it.dose_quantity}${it.dose_unit ? ` ${it.dose_unit}` : ''}` : '';
      const freqPart = it.frequency_code || it.frequency_text || '';
      const durPart = it.duration_value != null ? `${it.duration_value}${it.duration_unit || ''}` : '';
      const freqDur = freqPart && durPart ? `${freqPart} × ${durPart}` : [freqPart, durPart].filter(Boolean).join(' ');
      const rest = [dosePart, it.route, freqDur].filter(Boolean).join(', ');
      return `Rx. ${it.drug_name}${strength}${rest ? ` — ${rest}` : ''}`;
    })
    .join('\n');
}

/**
 * Reconciles a fresh AI reply (`fresh`, parsed the same way as today's
 * one-shot draft) against the current table (`current`), matching AI-owned
 * rows by drug name. Unchanged rows keep their position and `ai_proposal_id`;
 * changed rows keep their position but lose it (re-recorded, since the old
 * proposal now describes a line that no longer exists); brand-new drug
 * names are appended; an AI-owned row the fresh reply no longer mentions is
 * removed. Manual rows are never matched or touched. Returns the final
 * array (sequence renumbered) plus the indices into it that still need
 * `recordPrescriptionAiProposals` called for them.
 */
export function reconcileAiPrescriptionLines(
  current: PrescriptionItemInput[],
  fresh: PrescriptionItemInput[],
): { items: PrescriptionItemInput[]; toRecordIndices: number[] } {
  const ownedByName = new Map<string, number>();
  current.forEach((item, i) => {
    if (item.ai_proposal_id == null) return;
    const key = nameKey(item);
    if (!ownedByName.has(key)) ownedByName.set(key, i);
  });

  const result: PrescriptionItemInput[] = [...current];
  const consumed = new Set<number>();
  const toRecordIndices: number[] = [];

  for (const freshItem of fresh) {
    const key = nameKey(freshItem);
    const idx = ownedByName.get(key);
    if (idx != null) {
      consumed.add(idx);
      const existing = result[idx]!;
      if (linesEqual(existing, freshItem)) continue; // untouched, keeps its ai_proposal_id
      result[idx] = { ...freshItem, sequence: existing.sequence, substitution_allowed: existing.substitution_allowed ?? true, ai_proposal_id: null };
      toRecordIndices.push(idx);
    } else {
      result.push({ ...freshItem, ai_proposal_id: null });
      toRecordIndices.push(result.length - 1);
    }
  }

  // Any AI-owned row the fresh reply dropped (an implied removal, e.g.
  // "remove the amoxicillin") is removed — the draft table isn't saved or
  // activated until the doctor does so, matching "the thread updates the
  // table." Removing shifts indices, so they're recomputed below by object
  // identity (kept[i] is still the same reference as result[originalIdx],
  // since filter doesn't clone) rather than by the now-stale original index.
  const droppedIndices = new Set([...ownedByName.values()].filter((i) => !consumed.has(i)));
  const needsRecord = new Set(toRecordIndices.map((i) => result[i]));
  const kept = result.filter((_, i) => !droppedIndices.has(i));

  const finalToRecordIndices: number[] = [];
  kept.forEach((item, i) => { if (needsRecord.has(item)) finalToRecordIndices.push(i); });
  const items = kept.map((item, i) => ({ ...item, sequence: i + 1 }));

  return { items, toRecordIndices: finalToRecordIndices };
}
